/**
 * Full-Screen Letter Preview Modal
 * 
 * Large-format letter preview for comfortable reading and review.
 * Shows the actual generated dispute letter in a clean, readable format.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Download,
  Printer,
  ZoomIn,
  ZoomOut,
  Maximize2,
  FileText,
  CheckCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Send,
  FileSearch,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  AttachmentPreviewImage,
  getAttachmentDisplayLabel,
} from '../../utils/disputeEnvelopeAttachments';

/**
 * Sanitizes letter HTML content to prevent global style leakage.
 * The letter content often contains <style> tags targeting 'body' or 'html'
 * which can affect the entire application. This function scopes those styles
 * to only affect content inside the letter container.
 */
const sanitizeLetterContent = (htmlContent) => {
  if (typeof htmlContent !== 'string' || !htmlContent) return '';

  // Replace global selectors in <style> tags with scoped selectors
  // This prevents body { max-width: 8.5in } from affecting the real body element
  let sanitized = htmlContent;
  
  // Pattern 1: body { ... } -> .letter-content-scope { ... }
  sanitized = sanitized.replace(
    /(<style[^>]*>[\s\S]*?)(\bbody\s*\{)/gi,
    '$1.letter-content-scope {'
  );
  
  // Pattern 2: html { ... } -> .letter-content-scope { ... }
  sanitized = sanitized.replace(
    /(<style[^>]*>[\s\S]*?)(\bhtml\s*\{)/gi,
    '$1.letter-content-scope {'
  );
  
  // Pattern 3: * { ... } -> .letter-content-scope * { ... } (only at start of style block)
  // Be careful here - we only want to scope truly global * selectors
  sanitized = sanitized.replace(
    /(<style[^>]*>\s*)(\*\s*\{)/gi,
    '$1.letter-content-scope * {'
  );
  
  return sanitized;
};

function resolveLetterHtml(value) {
  if (typeof value === 'string') return value;
  if (value?.content && typeof value.content === 'string') return value.content;
  if (value?.html && typeof value.html === 'string') return value.html;
  if (value?.letter_content && typeof value.letter_content === 'string') return value.letter_content;
  return '';
}

// Bureau colors
const BUREAU_COLORS = {
  TransUnion: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  Experian: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  Equifax: 'bg-red-500/10 text-red-400 border-red-500/30',
};

const LetterPreviewModal = ({
  isOpen,
  onClose,
  letterContent,
  packageInfo,
  packages = [],
  currentPackageIndex = 0,
  onPackageChange,
  onDownload,
  onGenerate,
  isLoading = false,
  isGenerating = false,
  resolvedAttachments = [],
  supportingAttachments,
  supportingDocumentsTitle = 'Supporting Documents Included',
  noSupportingDocumentsLabel = 'No supporting documents attached.',
  onPostGridPreview,
  postGridPreviewLabel = 'Preview PostGrid PDF',
  onSendLetter,
  canSendLetter = false,
  sendLetterLabel = 'Send Letter',
}) => {
  const attachmentList = resolvedAttachments.length ? resolvedAttachments : (supportingAttachments || []);
  const [zoom, setZoom] = useState(100);
  const contentRef = useRef(null);
  const originalStylesRef = useRef(null);

  // Reset zoom when modal opens
  useEffect(() => {
    if (isOpen) {
      setZoom(100);
    }
  }, [isOpen]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && currentPackageIndex > 0) {
        onPackageChange?.(currentPackageIndex - 1);
      } else if (e.key === 'ArrowRight' && currentPackageIndex < packages.length - 1) {
        onPackageChange?.(currentPackageIndex + 1);
      } else if (e.key === '+' || e.key === '=') {
        setZoom(prev => Math.min(prev + 10, 150));
      } else if (e.key === '-') {
        setZoom(prev => Math.max(prev - 10, 50));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentPackageIndex, packages.length, onPackageChange, onClose]);

  const resolvedHtml = useMemo(() => resolveLetterHtml(letterContent), [letterContent]);

  // Memoize sanitized letter content to prevent global style leakage
  const sanitizedContent = useMemo(() => {
    return sanitizeLetterContent(resolvedHtml);
  }, [resolvedHtml]);

  // CRITICAL: Comprehensive body scroll/style lock with proper cleanup
  // Also handles cleanup of any leaked styles from letter content
  useEffect(() => {
    if (isOpen) {
      // Store ALL original computed styles that might be affected by leaked letter styles
      const bodyComputed = window.getComputedStyle(document.body);
      originalStylesRef.current = {
        // Inline styles we set
        overflow: document.body.style.overflow,
        paddingRight: document.body.style.paddingRight,
        position: document.body.style.position,
        width: document.body.style.width,
        height: document.body.style.height,
        top: document.body.style.top,
        left: document.body.style.left,
        maxWidth: document.body.style.maxWidth,
        minWidth: document.body.style.minWidth,
        htmlOverflow: document.documentElement.style.overflow,
        // Computed values to verify restoration
        computedWidth: bodyComputed.width,
        computedMaxWidth: bodyComputed.maxWidth,
      };
      
      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // Apply scroll lock
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      return () => {
        // CRITICAL: Restore ALL original styles on cleanup
        const original = originalStylesRef.current;
        if (original) {
          document.body.style.overflow = original.overflow || '';
          document.body.style.paddingRight = original.paddingRight || '';
          document.body.style.position = original.position || '';
          document.body.style.width = original.width || '';
          document.body.style.height = original.height || '';
          document.body.style.top = original.top || '';
          document.body.style.left = original.left || '';
          document.body.style.maxWidth = original.maxWidth || '';
          document.body.style.minWidth = original.minWidth || '';
          document.documentElement.style.overflow = original.htmlOverflow || '';
          
          // CRITICAL FIX: The letter content often contains <style> tags with global selectors
          // like "body { max-width: 8.5in }" that leak into the document and persist after modal close.
          // We need to find and remove these leaked styles.
          
          // 1. Remove any explicitly marked letter preview styles
          const injectedStyles = document.querySelectorAll('style[data-letter-preview]');
          injectedStyles.forEach(style => style.remove());
          
          // 2. Scan for and remove style tags that contain letter-specific rules
          // These are typically injected via dangerouslySetInnerHTML
          const allStyles = document.querySelectorAll('style');
          allStyles.forEach(style => {
            const content = style.textContent || '';
            // Check if this style contains letter-specific rules that affect body/html
            if (
              (content.includes('max-width: 8.5in') || content.includes('max-width:8.5in')) ||
              (content.includes('max-width: 816px') || content.includes('max-width:816px')) ||
              (content.includes("'Times New Roman'") && content.includes('body')) ||
              (content.includes('.consumer-identity') || content.includes('.letter-date'))
            ) {
              // This is a letter-injected style - remove it
              style.remove();
            }
          });
          
          // 3. Force cleanup: If body max-width was affected by letter styles, reset it
          const currentMaxWidth = window.getComputedStyle(document.body).maxWidth;
          if (currentMaxWidth === '816px' || currentMaxWidth === '8.5in') {
            // This indicates leaked letter styles - force override
            document.body.style.maxWidth = 'none';
            // Trigger layout recalculation
            void document.body.offsetHeight;
            // Reset to allow normal CSS to take over
            setTimeout(() => {
              document.body.style.maxWidth = original.maxWidth || '';
            }, 0);
          }
        }
        originalStylesRef.current = null;
      };
    }
  }, [isOpen]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow && resolvedHtml) {
      printWindow.document.write(resolvedHtml);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // IMPORTANT: AnimatePresence must wrap the conditional to enable exit animations
  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="letter-preview-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center"
          data-testid="letter-preview-modal"
        >
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-[rgba(6,28,20,0.85)] backdrop-blur-sm"
            onClick={onClose}
          />
          
          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="relative z-10 w-full h-full max-w-6xl max-h-[95vh] m-4 flex flex-col bg-stone-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-700"
          >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-stone-900">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Dispute Letter Preview
                </h2>
                {packageInfo && (
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={BUREAU_COLORS[packageInfo.target_bureau] || 'bg-slate-700'}>
                      {packageInfo.target_bureau || 'Unknown Bureau'}
                    </Badge>
                    {packageInfo.letter_url && (
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Ready
                      </Badge>
                    )}
                    <span className="text-slate-400 text-sm">
                      {packageInfo.findings_snapshot?.length || 0} findings
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Zoom Controls */}
              <div className="flex items-center gap-1 bg-stone-800 rounded-lg p-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setZoom(prev => Math.max(prev - 10, 50))}
                  className="h-8 w-8 p-0 text-slate-400 hover:text-white"
                  disabled={zoom <= 50}
                >
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-slate-400 text-sm w-12 text-center">{zoom}%</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setZoom(prev => Math.min(prev + 10, 150))}
                  className="h-8 w-8 p-0 text-slate-400 hover:text-white"
                  disabled={zoom >= 150}
                >
                  <ZoomIn className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Actions */}
              <Button
                variant="outline"
                size="sm"
                onClick={onPostGridPreview}
                disabled={!onPostGridPreview || isLoading}
                className="border-slate-600 text-slate-300 hover:text-white hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="letter-postgrid-preview-btn"
              >
                <FileSearch className="w-4 h-4 mr-2" />
                {postGridPreviewLabel}
              </Button>
              {canSendLetter && onSendLetter && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onSendLetter}
                  className="border-emerald-600/50 text-emerald-400 hover:text-white hover:bg-emerald-700/20"
                  data-testid="letter-send-slot-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendLetterLabel}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                disabled={!resolvedHtml}
                className="border-slate-600 text-slate-300 hover:text-white hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={onDownload}
                disabled={!resolvedHtml}
                className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="letter-download-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0 text-slate-400 hover:text-white"
                data-testid="letter-preview-close"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
          
          {/* Package Navigation (if multiple packages) */}
          {packages.length > 1 && (
            <div className="flex items-center justify-center gap-4 px-6 py-3 bg-stone-800/50 border-b border-slate-700">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPackageChange?.(currentPackageIndex - 1)}
                disabled={currentPackageIndex === 0}
                className="text-slate-400 hover:text-white"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2">
                {packages.map((pkg, idx) => (
                  <button
                    key={pkg.id}
                    onClick={() => onPackageChange?.(idx)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                      idx === currentPackageIndex
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'text-slate-400 hover:text-white hover:bg-slate-700'
                    }`}
                  >
                    {pkg.target_bureau}
                  </button>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPackageChange?.(currentPackageIndex + 1)}
                disabled={currentPackageIndex === packages.length - 1}
                className="text-slate-400 hover:text-white"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
          
          {/* Letter Content + Supporting Documents */}
          <div className="flex-1 overflow-hidden bg-stone-800/30 flex flex-col lg:flex-row min-h-0">
            <div className="flex-1 overflow-auto min-h-0">
              {isLoading ? (
                <div className="flex items-center justify-center h-full min-h-[320px]">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-4" />
                    <p className="text-slate-400">Loading letter preview...</p>
                  </div>
                </div>
              ) : resolvedHtml ? (
                <div className="p-8 flex justify-center">
                  <div 
                    style={{ 
                      transform: `scale(${zoom / 100})`,
                      transformOrigin: 'top center',
                      willChange: 'transform',
                    }}
                  >
                    <div 
                      ref={contentRef}
                      className="bg-white rounded-lg shadow-xl overflow-hidden"
                      style={{ 
                        width: '816px',
                        minHeight: '1056px',
                      }}
                    >
                      <div 
                        className="p-12 letter-content-scope"
                        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                        style={{
                          fontFamily: "'Times New Roman', Times, serif",
                          fontSize: '12pt',
                          lineHeight: '1.5',
                          color: '#000',
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full min-h-[320px]">
                  <div className="text-center max-w-md">
                    <div className="w-16 h-16 rounded-full bg-stone-800 flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-slate-500" />
                    </div>
                    <p className="text-slate-300 text-lg font-medium mb-2">No Letter Available</p>
                    <p className="text-slate-500 text-sm">
                      {onGenerate 
                        ? "Generate your dispute letter to preview and download"
                        : "Close this modal and use the Generate Letters button to create dispute letters for all packages."}
                    </p>
                    {onGenerate && (
                      <Button
                        onClick={onGenerate}
                        disabled={isGenerating}
                        className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <FileText className="w-4 h-4 mr-2" />
                            Generate Letter
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <aside
              className="lg:w-80 xl:w-96 shrink-0 border-t lg:border-t-0 lg:border-l border-slate-700 bg-stone-900/95 flex flex-col max-h-[40vh] lg:max-h-none"
              data-testid="supporting-documents-panel"
            >
              <div className="px-4 py-3 border-b border-slate-700">
                <h3 className="text-sm font-semibold text-white">{supportingDocumentsTitle}</h3>
                <p className="text-xs text-slate-500 mt-1">
                  {attachmentList.length
                    ? `${attachmentList.length} document${attachmentList.length === 1 ? '' : 's'} included in this packet`
                    : noSupportingDocumentsLabel}
                </p>
              </div>
              <div className="flex-1 overflow-auto p-4 space-y-3">
                {attachmentList.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-700 bg-stone-800/50 px-4 py-8 text-center">
                    <FileText className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">{noSupportingDocumentsLabel}</p>
                  </div>
                ) : (
                  attachmentList.map((attachment, index) => (
                    <div
                      key={attachment.document_id || attachment.id || `${attachment.type}-${index}`}
                      className="rounded-lg border border-slate-700 bg-stone-800/60 overflow-hidden"
                      data-testid={`supporting-doc-${attachment.type || index}`}
                    >
                      <div className="px-3 py-2 border-b border-slate-700/80">
                        <p className="text-xs font-medium text-emerald-400 truncate">
                          {getAttachmentDisplayLabel(attachment)}
                        </p>
                      </div>
                      <div className="h-28 bg-stone-950/50 flex items-center justify-center p-2">
                        <AttachmentPreviewImage
                          attachment={attachment}
                          alt={getAttachmentDisplayLabel(attachment)}
                          className="max-w-full max-h-full object-contain"
                          iconSize={24}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </aside>
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-slate-700 bg-stone-900">
            <p className="text-slate-500 text-sm">
              Press <kbd className="px-1.5 py-0.5 bg-stone-800 rounded text-slate-400">Esc</kbd> to close • 
              <kbd className="px-1.5 py-0.5 bg-stone-800 rounded text-slate-400 ml-1">+</kbd> / 
              <kbd className="px-1.5 py-0.5 bg-stone-800 rounded text-slate-400">-</kbd> to zoom
            </p>
            <p className="text-slate-500 text-sm">
              {packages.length > 1 && `Package ${currentPackageIndex + 1} of ${packages.length}`}
            </p>
          </div>
        </motion.div>
      </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LetterPreviewModal;
